import { ApiError } from '@/lib/api';
import { versionedClock, versionedDay } from '@/lib/versioned-labels';
import type {
  LinkEntryProblem,
  LinkError,
  LinkLifetime,
  LinkState,
  LinkSummary,
} from '@matter_extractor/shared';
import { assertNever, linkErrorSchema } from '@matter_extractor/shared';

/**
 * The words Réglages > Partage speaks (§5.1, §11.4), and the arithmetic behind
 * them.
 *
 * PURE, and one file, at the régime of `versioned-labels.ts` and
 * `persona-labels.ts`: a refusal worded in two places is worded two ways, and
 * `vitest.config.ts` leaves `src/routes/**` out of the coverage report, so a
 * rule that lived in the page would be a rule nothing measures.
 *
 * The two date helpers are IMPORTED from `versioned-labels.ts` rather than
 * copied: the month table is the administration's, `Intl` is deliberately not
 * used anywhere here (it answers by the locale of the machine, so a test and a
 * capture would disagree on two runners), and a second table would drift on the
 * first abbreviation somebody dislikes.
 */

/* ------------------------------------------------------------- the lifetime */

/** The closed list of §5.1, as an operator reads it. The identifiers stay code. */
export const LINK_LIFETIME_LABEL: Readonly<Record<LinkLifetime, string>> = {
  '24h': '24 h',
  '48h': '48 h',
  '7d': '7 jours',
  '30d': '30 jours',
};

/** The three states, for the pastille of a card. */
export const LINK_STATE_LABEL: Readonly<Record<LinkState, string>> = {
  active: 'actif',
  revoked: 'révoqué',
  expired: 'expiré',
};

/* ------------------------------------------------------------ the deadline */

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/**
 * How long is left, in ONE unit - « 21 jours », « 5 h », « 12 min ».
 *
 * One unit and never two, because the number is read at a glance beside a date
 * that already says the rest: « 21 jours et 3 heures » is more precision than
 * a decision about a link ever needs. Null once the deadline has passed, which
 * is the caller's cue to word the expiry instead.
 *
 * The day count ROUNDS DOWN, so « 1 jour » covers everything from 24 to 48
 * hours - the same reading as an age.
 */
export function linkRemaining(expiresAt: string, now: Date): string | null {
  const left = Date.parse(expiresAt) - now.getTime();
  if (Number.isNaN(left) || left <= 0) return null;
  if (left >= DAY_MS) {
    const days = Math.floor(left / DAY_MS);
    return `${days} jour${days > 1 ? 's' : ''}`;
  }
  if (left >= HOUR_MS) return `${Math.floor(left / HOUR_MS)} h`;
  const minutes = Math.ceil(left / MINUTE_MS);
  return minutes <= 1 ? 'moins d’une minute' : `${minutes} min`;
}

/**
 * The deadline line of a card (§11.4): « Expire dans 21 jours · le 1 oct. 2026 »,
 * « Révoqué le 3 sept. 2026 », « Expiré le 8 sept. 2026 ».
 *
 * A revoked link shows the date of the GESTURE and not its expiry: that date is
 * the only trace of when the door was shut, and the expiry of a link nobody can
 * open again says nothing.
 */
export function linkDeadlineSentence(link: LinkSummary, now: Date): string {
  switch (link.state) {
    case 'revoked':
      return `Révoqué le ${versionedDay(link.revokedAt ?? link.expiresAt)}`;
    case 'expired':
      return `Expiré le ${versionedDay(link.expiresAt)}`;
    case 'active': {
      const left = linkRemaining(link.expiresAt, now);
      // Under a day the hour is what an operator needs - « ce soir à 23:10 » in
      // the mockup - and past it the hour is noise beside a date.
      const when =
        Date.parse(link.expiresAt) - now.getTime() < DAY_MS
          ? `aujourd’hui à ${versionedClock(link.expiresAt)}`
          : `le ${versionedDay(link.expiresAt)}`;
      return left === null
        ? `Expiré le ${versionedDay(link.expiresAt)}`
        : `Expire dans ${left} · ${when}`;
    }
    default:
      return assertNever(link.state, 'link state');
  }
}

/** « 37 sessions entrées » (§11.4). Zero is worded, because a card always carries the line. */
export function sessionCountLabel(count: number): string {
  if (count === 0) return 'aucune session entrée';
  return count === 1 ? '1 session entrée' : `${count} sessions entrées`;
}

/**
 * The second half of the revocation confirmation, and it AGREES (§11.4).
 *
 * « Les sessions déjà commencées continuent » is a promise of §5.1, and it has
 * to survive a count of zero: composed by concatenation it read « Les aucune
 * session entrée déjà commencées continuent », which is the kind of sentence a
 * capture catches and a test never does. So the zero is its own sentence, and
 * the singular is its own too.
 */
export function revocationWarning(count: number): string {
  if (count === 0) return 'Aucune session n’est encore entrée par lui.';
  return count === 1
    ? 'La session déjà commencée continue.'
    : `Les ${count} sessions déjà commencées continuent.`;
}

/* ------------------------------------------------------------------ the URL */

/**
 * `<origine>/l/<secret>` (§5.1) - the thing the operator copies.
 *
 * The origin is a PARAMETER so this stays pure and testable: `window` is the
 * page's business, and a function that read it could not be run in a unit test
 * without a jsdom location.
 */
export function linkUrl(origin: string, secret: string): string {
  return `${origin.replace(/\/$/, '')}/l/${secret}`;
}

/* ------------------------------------------------------------- the refusals */

/**
 * The typed refusal of §5.1 if there is one, `null` otherwise.
 *
 * `ApiError.details` is kept unparsed by the client on purpose, so the parse
 * happens here - once - and a body that is not this shape reads as « no typed
 * refusal » rather than crashing the form. The régime of `versionedRefusalOf`.
 */
export function linkRefusalOf(error: unknown): LinkError | null {
  if (!(error instanceof ApiError)) return null;
  const parsed = linkErrorSchema.safeParse(error.details);
  return parsed.success ? parsed.data : null;
}

/**
 * What one refused row says, UNDER the row (§11.4).
 *
 * Worded from the `code` and never from the server's sentence, and each one
 * names the next gesture: an operator who reads « cette entrée ne résout pas »
 * learns nothing they can act on. `production-empty` names BOTH ways out,
 * because choosing `développement` on that very row is usually the one they
 * want.
 */
export function linkEntryProblemSentence(problem: LinkEntryProblem): string {
  switch (problem.code) {
    case 'production-empty':
      return `« ${problem.experienceName ?? 'Cette expérience'} » n’a jamais été passée en production : choisissez le côté dev, ou passez-la en production d’abord.`;
    case 'version-unknown':
      return `« ${problem.experienceName ?? 'Cette expérience'} » n’a pas cette version : choisissez-en une autre, ou latest.`;
    case 'pin-not-latest':
      return `Sur la vitrine, une entrée suit toujours latest : « ${problem.experienceName ?? 'cette expérience'} » ne peut pas être épinglée sur une version.`;
    case 'experience-retired':
      return `« ${problem.experienceName ?? 'Cette expérience'} » a été retirée : choisissez-en une autre, ou restaurez-la d’abord.`;
    case 'experience-unknown':
      return 'Cette expérience n’existe plus : choisissez-en une autre.';
    default:
      return assertNever(problem.code, 'link entry problem');
  }
}
